import { AI_REPORT_TYPES, type AiReportLanguage, type AiReportType } from '@erp/shared';
import { Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
  Input, Label, cn,
} from '../../components/shadcn/index.js';
import { Button } from '../../components/ui/index.js';

import { TYPE_ICON } from './reportMeta.js';
import { useCreateAiReport } from './useAiReports.js';

type PresetKey = 'today' | 'last7' | 'last30' | 'thisMonth' | 'lastMonth' | 'custom';

/** Local calendar date (the shop thinks in Iraq time, not UTC). */
function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function presetRange(preset: Exclude<PresetKey, 'custom'>): { from: string; to: string } {
  const now = new Date();
  const today = isoDate(now);
  const daysAgo = (n: number): string => {
    const d = new Date(now);
    d.setDate(d.getDate() - n);
    return isoDate(d);
  };
  switch (preset) {
    case 'today': return { from: today, to: today };
    case 'last7': return { from: daysAgo(6), to: today };
    case 'last30': return { from: daysAgo(29), to: today };
    case 'thisMonth': return { from: isoDate(new Date(now.getFullYear(), now.getMonth(), 1)), to: today };
    case 'lastMonth': return {
      from: isoDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
      to: isoDate(new Date(now.getFullYear(), now.getMonth(), 0)),
    };
  }
}

const PRESETS: PresetKey[] = ['today', 'last7', 'last30', 'thisMonth', 'lastMonth', 'custom'];

export function AiReportCreateDialog({ open, onOpenChange }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const { t, i18n } = useTranslation('aiReports');
  const navigate = useNavigate();
  const create = useCreateAiReport();

  const [type, setType] = useState<AiReportType>('business_summary');
  const [preset, setPreset] = useState<PresetKey>('last30');
  const [custom, setCustom] = useState(() => presetRange('last30'));
  const [language, setLanguage] = useState<AiReportLanguage>(i18n.language.startsWith('ar') ? 'ar' : 'en');
  const [title, setTitle] = useState('');
  const [question, setQuestion] = useState('');

  const range = preset === 'custom' ? custom : presetRange(preset);
  // Mirrors the server schema: ordered range, at most one year, question when asking.
  const spanOk = !!range.from && !!range.to && range.from <= range.to
    && Date.parse(`${range.to}T00:00:00Z`) - Date.parse(`${range.from}T00:00:00Z`) <= 366 * 86_400_000;
  const canSubmit = !create.isPending && spanOk
    && (type !== 'custom_question' || question.trim().length > 0);

  const submit = async (): Promise<void> => {
    try {
      const report = await create.mutateAsync({
        type,
        periodFrom: range.from,
        periodTo: range.to,
        language,
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(type === 'custom_question' ? { question: question.trim() } : {}),
      });
      onOpenChange(false);
      navigate(`/reports/ai/${report.id}`);
    } catch {
      // 429 (20/hour cap), 403, or a snapshot 500 — without this the dialog just sits there.
      toast.error(t('server', { ns: 'errors', defaultValue: 'Something went wrong. Please try again.' }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-brand" aria-hidden />
            {t('aiReports.create.title', { defaultValue: 'New AI report' })}
          </DialogTitle>
          <DialogDescription>{t('aiReports.subtitle', { defaultValue: 'AI-written analytical reports over your real numbers' })}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          {/* Type cards */}
          <div role="radiogroup" aria-label={t('aiReports.create.typeLabel', { defaultValue: 'Report type' })} className="grid gap-2 sm:grid-cols-2">
            {AI_REPORT_TYPES.map((k) => {
              const Icon = TYPE_ICON[k];
              const selected = type === k;
              return (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setType(k)}
                  className={cn(
                    'flex items-start gap-3 rounded-lg border p-3 text-start transition-colors',
                    selected ? 'border-brand bg-brand/5 ring-1 ring-brand' : 'border-border hover:border-brand/50',
                  )}
                >
                  <span className={cn('mt-0.5 grid h-8 w-8 flex-none place-items-center rounded-md', selected ? 'bg-brand text-brand-contrast' : 'bg-bg-muted text-fg-muted')}>
                    <Icon className="h-4 w-4" aria-hidden />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold">{t(`aiReports.type.${k}`, { defaultValue: k })}</span>
                    <span className="mt-0.5 block text-xs leading-5 text-fg-muted">{t(`aiReports.typeHint.${k}`, { defaultValue: '' })}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* Question (custom type only) */}
          {type === 'custom_question' && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ai-report-question">{t('aiReports.create.questionLabel', { defaultValue: 'Your question' })}</Label>
              <textarea
                id="ai-report-question"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder={t('aiReports.create.questionPlaceholder', { defaultValue: 'e.g. Which products should I push next month, and to whom?' })}
                maxLength={2000}
                rows={3}
                className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm outline-none transition-colors placeholder:text-fg-subtle focus:border-brand focus:ring-2 focus:ring-brand/30"
              />
            </div>
          )}

          {/* Period presets */}
          <div className="flex flex-col gap-1.5">
            <Label>{t('aiReports.create.periodLabel', { defaultValue: 'Period' })}</Label>
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => {
                    setPreset(p);
                    if (p !== 'custom') setCustom(presetRange(p));
                  }}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    preset === p ? 'border-brand bg-brand text-brand-contrast' : 'border-border text-fg-muted hover:border-brand/50 hover:text-fg',
                  )}
                >
                  {t(`aiReports.create.preset.${p}`, { defaultValue: p })}
                </button>
              ))}
            </div>
            {preset === 'custom' && (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Label htmlFor="ai-report-from" className="text-xs text-fg-muted">{t('aiReports.create.from', { defaultValue: 'From' })}</Label>
                <Input id="ai-report-from" type="date" value={custom.from} max={custom.to || undefined}
                  onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} className="w-40" />
                <Label htmlFor="ai-report-to" className="text-xs text-fg-muted">{t('aiReports.create.to', { defaultValue: 'To' })}</Label>
                <Input id="ai-report-to" type="date" value={custom.to} min={custom.from || undefined}
                  onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} className="w-40" />
              </div>
            )}
          </div>

          {/* Language + optional title */}
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-1.5">
              <Label>{t('aiReports.create.languageLabel', { defaultValue: 'Report language' })}</Label>
              <div className="inline-flex overflow-hidden rounded-md border border-border" role="radiogroup">
                {(['ar', 'en'] as const).map((l) => (
                  <button
                    key={l}
                    type="button"
                    role="radio"
                    aria-checked={language === l}
                    onClick={() => setLanguage(l)}
                    className={cn(
                      'px-4 py-1.5 text-sm font-medium transition-colors',
                      language === l ? 'bg-brand text-brand-contrast' : 'bg-bg text-fg-muted hover:text-fg',
                    )}
                  >
                    {l === 'ar' ? 'العربية' : 'English'}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex min-w-56 flex-1 flex-col gap-1.5">
              <Label htmlFor="ai-report-title">{t('aiReports.create.titleLabel', { defaultValue: 'Title (optional)' })}</Label>
              <Input
                id="ai-report-title"
                value={title}
                maxLength={200}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('aiReports.create.titlePlaceholder', { defaultValue: 'Derived from type and period when empty' })}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" type="button" onClick={() => onOpenChange(false)}>
            {t('actions.cancel', { ns: 'common', defaultValue: 'Cancel' })}
          </Button>
          <Button type="button" onClick={() => void submit()} disabled={!canSubmit} loading={create.isPending}>
            <Sparkles className="me-1.5 h-4 w-4" aria-hidden />
            {t('aiReports.create.submit', { defaultValue: 'Generate report' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
